/**
 * PURPOSE: Configuración de elegibles Rugby por evento-deporte (FTR-0015): regla, plazo/cambios y plazo por fecha.
 * RESPONSIBILITIES: Regla (perfil SEVENS/XV/M18/CUSTOM, titulares AT_MOST/EXACT, máximo de elegibles, WARN/BLOCK por
 *   límite) con "Guardar sin confirmar" y "Guardar y confirmar" (sin confirmar, los delegados no envían); plazo por
 *   defecto en minutos antes del inicio, cambios permitidos y comportamiento al agotarlos (sin él, los delegados no
 *   envían y los plazos por partido no rigen). El plazo por fecha del fixture (dos modos) vive aparte en
 *   RugbyFixtureDeadlinePanel.jsx. Confirmaciones con window.confirm (patrón de la pestaña).
 * COLLABORATORS: rugbyLineupsService.js, rugbyLineups.utils.js, CompanyRugbyLineupsTab.jsx,
 *   RugbyFixtureDeadlinePanel.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/RugbyLineupConfigPanels.jsx
 */
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import { saveRugbyLineupPolicy, saveRugbyLineupRule } from "../../services/rugbyLineupsService.js";
import {
  COUNT_MODE_LABELS,
  ENFORCEMENT_LABELS,
  EXHAUSTION_LABELS,
  RULE_PROFILE_LABELS,
  applyProfile,
  buildPolicyPayload,
  buildRulePayload,
  describeRuleStatus,
  getRugbyErrorMessage,
  policyFormFrom,
  ruleFormFrom,
  validatePolicyForm,
  validateRuleForm,
} from "./rugbyLineups.utils.js";

const TONES = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  rose: "border-rose-200 bg-rose-50 text-rose-800",
  slate: "border-slate-200 bg-slate-50 text-slate-700",
};
export const StatusNote = ({ tone = "slate", children }) => (
  <div role="status" className={`rounded-lg border px-4 py-2.5 text-sm font-semibold ${TONES[tone] || TONES.slate}`}>
    {children}
  </div>
);

const Field = ({ label, error, children }) => (
  <label className="text-xs font-semibold text-slate-600">
    {label}
    <div className="mt-1">{children}</div>
    {error && <span className="mt-1 block font-normal text-rose-700">{error}</span>}
  </label>
);

const selectClass = "w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-normal";

export const RugbyLineupRulePanel = ({ scope, ruleStatus, profiles, onSaved }) => {
  const [form, setForm] = useState(() => ruleFormFrom(ruleStatus, profiles));
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(ruleFormFrom(ruleStatus, profiles)), [ruleStatus, profiles]);
  const errors = validateRuleForm(form);
  const status = describeRuleStatus(ruleStatus);
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const save = async (confirm) => {
    if (Object.keys(errors).length) return;
    if (confirm) {
      const blocking = [form.starterEnforcement, form.eligibleEnforcement].includes("BLOCK");
      const message = blocking
        ? "Al confirmar, los envíos que no cumplan un límite en Bloqueo serán rechazados. ¿Confirmar la regla?"
        : "¿Confirmar la regla para este evento-deporte?";
      if (!window.confirm(message)) return;
    } else if (ruleStatus?.status === "CONFIRMED" && !window.confirm("Guardar sin confirmar cierra el envío de los delegados hasta que vuelvas a confirmar. ¿Continuar?")) {
      return;
    }
    setSaving(true);
    try {
      await saveRugbyLineupRule(scope, buildRulePayload(form, { confirm }));
      toast.success(confirm ? "Regla confirmada." : "Regla guardada sin confirmar.");
      onSaved();
    } catch (error) {
      toast.error(getRugbyErrorMessage(error, "No se pudo guardar la regla."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-base font-bold text-slate-800">Regla de alineación</h3>
      <p className="mb-3 text-xs text-slate-500">
        Sin una regla confirmada los delegados no pueden enviar; Sevens 7/12 en advertencia es solo una vista previa para la administración. El bloqueo
        aplica después de confirmar.
      </p>
      <StatusNote tone={status.tone}>{status.text}</StatusNote>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <Field label="Perfil">
          <select value={form.profileKey} onChange={(event) => setForm((current) => applyProfile(current, event.target.value, profiles))} className={selectClass}>
            {Object.entries(RULE_PROFILE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Titulares" error={errors.starterLimit}>
          <input type="number" min={1} value={form.starterLimit} onChange={set("starterLimit")} disabled={form.profileKey !== "CUSTOM"} className={`${selectClass} disabled:bg-slate-100`} />
        </Field>
        <Field label="Máximo de elegibles" error={errors.eligibleMax}>
          <input type="number" min={1} value={form.eligibleMax} onChange={set("eligibleMax")} disabled={form.profileKey !== "CUSTOM"} className={`${selectClass} disabled:bg-slate-100`} />
        </Field>
        <Field label="Modo de titulares">
          <select value={form.starterCountMode} onChange={set("starterCountMode")} className={selectClass}>
            {Object.entries(COUNT_MODE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Límite de titulares">
          <select value={form.starterEnforcement} onChange={set("starterEnforcement")} className={selectClass}>
            {Object.entries(ENFORCEMENT_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Límite de elegibles">
          <select value={form.eligibleEnforcement} onChange={set("eligibleEnforcement")} className={selectClass}>
            {Object.entries(ENFORCEMENT_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" disabled={saving || Object.keys(errors).length > 0} onClick={() => save(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
          Guardar sin confirmar
        </button>
        <button type="button" disabled={saving || Object.keys(errors).length > 0} onClick={() => save(true)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-slate-300">
          {saving ? "Guardando..." : "Guardar y confirmar"}
        </button>
      </div>
    </section>
  );
};

export const RugbyLineupPolicyPanel = ({ scope, policy, onSaved }) => {
  const [form, setForm] = useState(() => policyFormFrom(policy));
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(policyFormFrom(policy)), [policy]);
  const errors = validatePolicyForm(form);
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const save = async (event) => {
    event.preventDefault();
    if (Object.keys(errors).length) return;
    setSaving(true);
    try {
      await saveRugbyLineupPolicy(scope, buildPolicyPayload(form));
      toast.success("Plazo y cambios guardados.");
      onSaved();
    } catch (error) {
      toast.error(getRugbyErrorMessage(error, "No se pudo guardar el plazo."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-base font-bold text-slate-800">Plazo y cambios</h3>
      <p className="mb-3 text-xs text-slate-500">
        Plazo por defecto en minutos antes del inicio (se recalcula al reprogramar). Sin esta configuración los delegados no pueden enviar y los plazos
        por partido o por institución no rigen.
      </p>
      {!policy && <StatusNote tone="amber">Sin configurar · envío de delegados cerrado hasta guardar el plazo y los cambios.</StatusNote>}
      <form onSubmit={save} className="mt-3 grid gap-3 sm:grid-cols-3">
        <Field label="Minutos antes del inicio" error={errors.minutesBeforeKickoff}>
          <input type="number" min={0} value={form.minutesBeforeKickoff} onChange={set("minutesBeforeKickoff")} className={selectClass} />
        </Field>
        <Field label="Cambios permitidos (tras el primer envío)" error={errors.allowedChanges}>
          <input type="number" min={0} value={form.allowedChanges} onChange={set("allowedChanges")} className={selectClass} />
        </Field>
        <Field label="Al agotar los cambios" error={errors.exhaustionBehavior}>
          <select value={form.exhaustionBehavior} onChange={set("exhaustionBehavior")} className={selectClass}>
            {Object.entries(EXHAUSTION_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-3">
          <button type="submit" disabled={saving || Object.keys(errors).length > 0} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-slate-300">
            {saving ? "Guardando..." : "Guardar plazo y cambios"}
          </button>
        </div>
      </form>
    </section>
  );
};
